import {
  handleSubmitMultilingualForm,
  useCreateArtist,
  useGetArtistFormData,
  useUpdateArtist,
} from "@/api/cms";
import { CmsPageAppearanceConfig } from "@/components/cms/theme/CmsPageAppearanceConfig";
import {
  ArtistFormProps,
  BibliographyForm,
  BiographyForm,
  CollectionsForm,
  ExhibitionsForm,
  NewArtistForm,
} from "@/components/cms/ArtistForm.tsx";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs.tsx";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast.ts";
import {
  constructCmsLocaleDict,
  getMainLanguage,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import {
  artistContentSchema,
  cmsPageLocaleBaseSchemaShape,
  withPageNameRequiredWhenPublished,
} from "@/lib/cms/cms-types";
import {
  buildCmsPagePatchPayload,
  pageThemeFromDto,
} from "@/lib/cms/page-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormFieldVisibilityProvider } from "@/context/FormFieldVisibilityContext";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const itemFormSchema = withPageNameRequiredWhenPublished(
  cmsPageLocaleBaseSchemaShape.merge(
    artistContentSchema.omit({
      id: true,
    }),
  ),
).optional();

const artistFormSchema = multilingualFieldSchema(itemFormSchema);

type FormData = z.infer<typeof artistFormSchema>;

const ArtistCMS = () => {
  const { t } = useTranslation();
  const { websiteLanguages } = useCmsWebsiteLanguages();
  const { toast } = useToast();

  const create = useCreateArtist();
  const update = useUpdateArtist();

  const { data: artistData } = useGetArtistFormData();

  const defaultValues: FormData = useMemo(
    () =>
      constructCmsLocaleDict((lang) => {
        const artist = artistData?.[lang];
        return {
          ...artist?.content,
          publishedBibliography:
            artist?.content?.publishedBibliography ?? false,
          publishedBiography: artist?.content?.publishedBiography ?? false,
          publishedCollections: artist?.content?.publishedCollections ?? false,
          publishedExhibitions: artist?.content?.publishedExhibitions ?? false,
          published: artist?.published ?? false,
          pageName: artist?.pageName ?? "",
          ...pageThemeFromDto(artist),
        } satisfies z.infer<typeof itemFormSchema>;
      }, websiteLanguages),
    [artistData, websiteLanguages],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(artistFormSchema),
    defaultValues,
  });

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (
      !hasInitialized.current &&
      artistData != null &&
      defaultValues &&
      Object.keys(defaultValues).length > 0
    ) {
      form.reset(defaultValues);
      hasInitialized.current = true;
    }
  }, [defaultValues, form, artistData]);

  const onSubmit = (submittedData: FormData) => {
    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = artistData?.[key as SupportedLocale];
      if (!localeData) return;

      const mutation = baseData?.content.id ? update : create;

      const {
        pageName,
        published,
        pageBackgroundColorId,
        pageTextColorMode,
        pageTextColorId,
        ...content
      } = localeData;
      const pageNameStr = pageName ?? "";

      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: buildCmsPagePatchPayload({
            pageName: pageNameStr,
            published,
            pageBackgroundColorId,
            pageTextColorMode,
            pageTextColorId,
            content,
          }),
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("homepageSuccessMessage"),
              duration: 4000,
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("error"),
              description: t("homepageErrorMessage"),
              variant: "error",
              duration: 4000,
            });
          },
        },
      );
    });
  };

  const tabList = useMemo(
    () =>
      [
        {
          label:
            artistData?.[getMainLanguage()]?.pageName ??
            t("cms.artist.tabs.artist"),
          component: NewArtistForm,
          value: "artist",
          publishedSwitch: "published",
        },
        {
          label:
            artistData?.[getMainLanguage()]?.content.biographySectionTitle ??
            t("cms.artist.tabs.biography"),
          component: BiographyForm,
          value: "biography",
          publishedSwitch: "publishedBiography",
        },
        {
          label:
            artistData?.[getMainLanguage()]?.content.collectionsSectionTitle ??
            t("cms.artist.tabs.collections"),
          component: CollectionsForm,
          value: "collections",
          publishedSwitch: "publishedCollections",
        },
        {
          label:
            artistData?.[getMainLanguage()]?.content.exhibitionsSectionTitle ??
            t("cms.artist.tabs.exhibitions"),
          component: ExhibitionsForm,
          value: "exhibitions",
          publishedSwitch: "publishedExhibitions",
        },
        {
          label:
            artistData?.[getMainLanguage()]?.content.bibliographySectionTitle ??
            t("cms.artist.tabs.bibliography"),
          component: BibliographyForm,
          value: "bibliography",
          publishedSwitch: "publishedBibliography",
        },
      ] satisfies {
        label: string;
        value: string;
        publishedSwitch: string;
        component: (props: ArtistFormProps) => JSX.Element;
      }[],
    [t, artistData],
  );

  return (
    <CMSLayout pageName={t("cmsTabArtiste")} formIds={["artist"]}>
      <FormProvider {...form}>
        <FormFieldVisibilityProvider formKey="cms-artist">
        <CmsPageAppearanceConfig formId="artist" />
        <Tabs className="w-full" defaultValue="artist" asChild>
          <form
            onSubmit={handleSubmitMultilingualForm(form, artistData, onSubmit)}
            id="artist"
            className="flex flex-col gap-4"
          >
            <TabsList>
              {tabList.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabList.map((tab) => {
              const Comp = tab.component;
              return (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className="inline-flex gap-8 w-full data-[state=inactive]:hidden"
                >
                  <DoubleMultilingualField
                    corner={(lang) => (
                      <Switch
                        dispLabel={false}
                        name={`${lang}.${tab.publishedSwitch}`}
                      />
                    )}
                    render={(lang) => <Comp language={lang} />}
                  />
                </TabsContent>
              );
            })}
          </form>
        </Tabs>
              </FormFieldVisibilityProvider>
      </FormProvider>
    </CMSLayout>
  );
};

export default ArtistCMS;
